import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { ManagedTabService } from './managed-tab.service';


declare const BoldBI: any;
export interface BoldBiEmbedConfig {
  containerId: string;
  serverUrl: string;
  dashboardId: string;
  token: string;
  /** Regtrack customer ID — forwarded to the authorization endpoint so the backend can look up the correct BoldBI secret_key. */
  customerId: number;
}


/**
 * Service responsible for loading the BoldBI embed SDK, creating and initializing embedded dashboards,
 * destroying existing embed instances before switching dashboards, and intercepting navigation links
 * to open dashboard tabs in a new browser window.
 *
 * @service
 * @providedIn root
 * @example
 *   constructor(private boldBiTabService: BoldBiTabService) {}
 *   await boldBiTabService.loadScript();
 *   const instance = boldBiTabService.createAndLoad(config);
 *   // Later, when switching dashboards:
 *   boldBiTabService.destroyInstance(instance);
 *
 * @method loadScript
 *   Loads the BoldBI SDK script dynamically into the document head and resolves when available.
 * @method createAndLoad
 *   Creates a BoldBI embedded dashboard instance and loads it. Returns the raw BoldBI instance
 *   so callers can call .destroy() before switching dashboards.
 * @method destroyInstance
 *   Safely destroys an existing BoldBI embed instance, freeing its DOM bindings and listeners.
 * @method handleNavigate
 *   Handles BoldBI navigation events by extracting a dashboard UUID and opening it in a new tab.
 */
@Injectable({
  providedIn: 'root'
})
export class BoldBiTabService {

  /**
   * @param managedTabService - Tracks tabs this service opens so they close with
   *   their opener tab and on logout.
   */
  constructor(private managedTabService: ManagedTabService) {}

   /**
   * Loads the BoldBI SDK script into the document if it is not already present.
   * @example
   * loadScript()
   * Promise<void>
   * @returns {Promise<void>} Resolves when the BoldBI SDK script has loaded successfully, or rejects if loading fails.
   **/
   loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('boldbi-sdk')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = 'boldbi-sdk';
      // script.src = 'https://cdn.boldbi.com/embedded-sdk/v14.2.4/boldbi-embed.js';
      // script.src = 'https://cdn.boldbi.com/embedded-sdk/v16.1.81/boldbi-embed.js';
      script.src = 'https://cdn.boldbi.com/embedded-sdk/v16.2.6/boldbi-embed.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load BoldBI SDK'));
      document.head.appendChild(script);
    });
  }


  /**
  * Creates and loads a Bold BI dashboard embed instance using the provided configuration.
  *
  * Returns the raw BoldBI instance object. Callers should retain this reference and pass
  * it to destroyInstance() before switching to a different dashboard, to prevent memory
  * leaks and stale event listeners from the previous embed.
  *
  * The embed is created with the dashboard comment icon enabled
  * (`settings.viewer.comment`, which Bold BI defaults to false) and with the
  * AI Assistant "Summarize" option enabled at the dashboard level only; the
  * per-widget hover Summarize icon is intentionally disabled. Summarize only
  * takes effect when the AI service is also configured and enabled at the
  * BoldBI tenant/site level, and commenting requires the embed user to hold
  * comment permission on the dashboard server-side.
  *
  * @example
  * const instance = createAndLoad({ serverUrl: '...', dashboardId: '...', containerId: 'el', token: '...' });
  * // Later, on dashboard switch:
  * destroyInstance(instance);
  *
  * @param {BoldBiEmbedConfig} config - Configuration object containing server URL, dashboard ID,
  *   container element ID, and the Bearer authorization token.
  * @returns {any} The raw BoldBI embed instance (typed as `any` because BoldBI has no published
  *   TypeScript types — this is the accepted pattern for this SDK in this codebase).
  **/
  createAndLoad(config: BoldBiEmbedConfig): any /* BoldBI instance */ {
    const dashboard = BoldBI.create({
      serverUrl: config.serverUrl,
      dashboardId: config.dashboardId,
      embedContainerId: config.containerId,
      embedType: BoldBI.EmbedType.Component,
      environment: BoldBI.Environment.Enterprise,
      authorizationServer: {
        url: `${environment.API_BASE_URL}/login/bold-bi-authorization`,
        // Token is read here — at embed creation time — not at component init
        headers: {
          Authorization: `Bearer ${config.token}`,
          'X-Customer-ID': String(config.customerId)
        }
      },
      // Enables the AI Assistant "Summarize" option at the dashboard level only.
      // widget:false suppresses the AI Summarize icon that otherwise appears on
      // every widget (grids, charts, number cards) on hover. Requires the AI
      // service to also be configured and enabled at the BoldBI tenant/site
      // level — this flag has no effect otherwise. The nested
      // summary.{widget,dashboard} structure requires SDK/server v13.1+.
      settings: {
        // Shows the dashboard comment icon (Bold BI defaults this to false).
        // Posting still depends on the embed user minted by the authorization
        // server having comment permission on the dashboard.
        viewer: {
          comment: true
        },
        aiAssistant: {
          enabled: true,
          summary: {
            enabled: true,
            widget: false,
            dashboard: true
          }
        }
      },
      beforeNavigateUrlLinking: (args: any) => this.handleNavigate(args)
    });

    dashboard.loadDashboard();
    return dashboard;
  }

  /**
   * Destroys an existing BoldBI embed instance.
   *
   * Call this before creating a new embed in the same container (e.g. when the user
   * switches dashboards). Destroying removes DOM event listeners and iframe elements
   * managed by the BoldBI SDK, preventing memory leaks.
   *
   * @param instance - The BoldBI instance returned by a previous createAndLoad() call.
   *   A null/undefined value is silently ignored (safe to call unconditionally).
   */
  destroyInstance(instance: any): void {
    if (!instance) return;
    try {
      instance.destroy();
    } catch (err) {
      // BoldBI.destroy() can throw if the instance was already cleaned up internally.
      // We swallow the error here — the embed is gone either way.
      console.warn('BoldBI destroyInstance: suppressed error during destroy', err);
    }
  }

  /**
   * Intercepts a BoldBI drill-down link and reopens it as a managed tab.
   *
   * The SDK's own navigation is cancelled unconditionally, so a link whose
   * dashboard UUID cannot be extracted is logged and dropped rather than
   * falling through to the SDK. The replacement tab is tracked so it closes
   * with its opener and on logout — see the inline note.
   * @param args - The BoldBI `beforeNavigateUrlLinking` event; `linkInfo.url` carries the target.
   * @returns Nothing; cancels the event and may open a new tab.
   */
   handleNavigate(args: any): void {
    args.cancel = true;
    const rawUrl = decodeURI(args.linkInfo?.url ?? '');
    const uuidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
    const match = rawUrl.match(uuidRegex);
    if (!match) {
      console.error('BoldBI nav: could not extract dashboard UUID from', rawUrl);
      return;
    }
    // Tracked so this drill-down tab closes with the RegAnalytics tab that spawned
    // it, and on logout. Same plain new-tab open as before.
    this.managedTabService.openManagedTab(`/dashboard-tab?id=${match[0]}`);
  }
}
